import { Component, Input, ViewChild, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormControl, NgForm } from '@angular/forms';
import { MatPaginator, MatSort, MatTableDataSource } from '@angular/material';
import * as XLSX from 'xlsx';
import { AuthProvider } from '../../providers/auth/auth';
import _ from 'lodash';
import { BehaviorSubject, Subject } from 'rxjs';
import { AlertController, LoadingController } from 'ionic-angular';
import { UtilityProvider } from '../../providers/utility/utility';
import { PatientProvider } from '../../providers/patient/patient';
import { DatabaseProvider } from '../../providers/database/database';
import { TaskProvider } from '../../providers/task/task';

@Component({
  selector: 'patient-dispensing',
  templateUrl: 'patient-dispensing.html'
})
export class PatientDispensingComponent implements OnInit {
  private ngUnsubscribe: Subject<any> = new Subject();
  program_id: string;
  program_name: string;
  patient: any;
  exportjson: any;
  private _program_id = new BehaviorSubject<any>([]);
  private _program_name = new BehaviorSubject<any>([]);
  private _patient = new BehaviorSubject<any>([]);

  @Input()
  set programId(value) { this._program_id.next(value); }
  get programId() { return this._program_id.getValue(); }

  @Input()
  set programName(value) { this._program_name.next(value); }
  get programName() { return this._program_name.getValue(); }

  @Input()
  set patientData(value) { this._patient.next(value); }
  get patientData() { return this._patient.getValue(); }

  userid: string;
  user_fullname: string;
  user: any;
  pharmacies: any[] = [];
  therapies: any[] = [];
  dosages: any[] = [];
  dispensingPanelStatus = false;
  existing = false;
  dispensing_id: string;

  addeditDispensingForm: FormGroup; 

  displayedColumns = ['pharmacy.name', 'drug_type', 'dose', 'units',  'quantity', 'fill_date', 'notes', 'createdBy', 'createdAt', 'actions'];
  dataSource: any;

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild('formDispensingDirective') private formDispensingDirective: NgForm;

  constructor(
    private readonly auth: AuthProvider,
    private dbPatient: PatientProvider,
    private db: DatabaseProvider,
    private dbTask: TaskProvider,
    private fb: FormBuilder,
    public alertCtrl: AlertController,
    public utility: UtilityProvider,
    public loadingCtrl: LoadingController
    ) {
      this.createForm();

      this.auth.getCurrentUser().then(user => {
        this.userid = user.id;
        this.user_fullname = user.first_name + ' ' + user.last_name;
        this.user = { id: user.id, name: user.first_name + ' ' + user.last_name };

        this.db
        .getPharmaciesByProgram(this.program_id)
        .snapshotChanges()
        .map(actions => {
          return actions.map(a => {
            const data = a.payload.doc.data() as any;
            data.id = a.payload.doc.id;
            return data;
          });
        })
        .takeUntil(this.ngUnsubscribe)
        .subscribe((pharmacies) => {
          this.pharmacies = pharmacies;
        });

        this.db
        .getTherapyByProgram(this.program_id)
        .snapshotChanges()
        .map(actions => {
          return actions.map(a => {
            const data = a.payload.doc.data() as any;
            data.id = a.payload.doc.id;
            return data;
          });
        })
        .takeUntil(this.ngUnsubscribe)
        .subscribe((therapies) => {
          this.therapies = therapies;
          this.dosages = _.filter(this.therapies, therapy => therapy.name === this.patient.therapy)[0]['dispensing'];
        });

        this.dbPatient
        .listDispensing(this.program_id, this.patient.id)
        .snapshotChanges()
        .map(actions => {
          return actions.map(a => {
            const data = a.payload.doc.data() as any;
            data.id = a.payload.doc.id;
            return data;
          });
        })
        .takeUntil(this.ngUnsubscribe)
        .subscribe(dispensing => {
          // console.log('Dispensing: ', dispensing);
          this.exportjson = dispensing;
          this.dataSource = new MatTableDataSource(dispensing);
          this.dataSource.paginator = this.paginator;
          this.dataSource.sort = this.sort;
        });
      });
    }

  ngOnInit()  {
    this._program_id
    .takeUntil(this.ngUnsubscribe)
    .subscribe(x => {
      // console.log('Program ID on Summary: ', x)
        this.program_id = x
    });

    this._program_name
    .takeUntil(this.ngUnsubscribe)
    .subscribe(x => {
      // console.log('Program Name on Summary: ', x)
        this.program_name = x
    });

    this._patient
    .takeUntil(this.ngUnsubscribe)
    .subscribe(x => {
      // console.log('Patient on Summary: ', x)
        this.patient = x
    });
  }

  ngOnDestroy() {
    console.log('Leave patient dispensing and destroy subscriptions');
    this._patient.unsubscribe();
    this._program_id.unsubscribe();
    this._program_name.unsubscribe();
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }


  applyFilter(filterValue: string) {
    filterValue = filterValue.trim(); // Remove whitespace
    filterValue = filterValue.toLowerCase(); // Datasource defaults to lowercase matches
    this.dataSource.filter = filterValue;
  }

  export() {
    const workBook = XLSX.utils.book_new(); // create a new blank book
    const workSheet = XLSX.utils.json_to_sheet(this.exportjson);

    XLSX.utils.book_append_sheet(workBook, workSheet, 'Dispensing'); // add the worksheet to the book
    XLSX.writeFile(workBook, 'dispensing.xlsx'); // initiate a file download in browser
  }

  createForm()  {
    this.addeditDispensingForm = this.fb.group({
      'pharmacy': [null, [Validators.required]],
      'drug_type': [null, [Validators.required]],
      'dose': [null, [Validators.required]],
      'quantity': [null, [Validators.required]],
      'units': [null, [Validators.required]],
      'quantity_remaining': [null],
      'fill_date': [null, [Validators.required]],
      'notes': [null]
    });
    this.addeditDispensingForm.controls['fill_date'].setValue(new Date());
  }

  save()  {
    const loading = this.loadingCtrl.create({});
    loading.present();
    let dispensing = this.addeditDispensingForm.value;
    dispensing.therapy = this.patient.therapy;
    dispensing.indication = this.patient.indication;
    const patient = {
      id: this.patient.id,
      first_name: this.patient.first_name,
      last_name: this.patient.last_name,
      program_patient_id: this.patient.program_patient_id
    }
    if(this.patient.dispensing_first_fill_date)  {
      dispensing.first_fill_date = this.patient.dispensing_first_fill_date;
    }
    dispensing = { ...dispensing, patient: patient, doctor: this.patient.doctor ? this.patient.doctor : null, clinic: this.patient.clinic ? this.patient.clinic : null };
    if (!this.existing) {
      this.dbPatient.addDispensing(this.program_id, this.patient, dispensing, this.user).then(() => {
        this.utility.presentToast('Dispening entry was successfully saved');
        this.cancel();
        if (dispensing.quantity_remaining && dispensing.quantity_remaining <= 15 && this.patient.ncm) {
          const task = { 
            assigneduser_id: this.patient.ncm.id,
            assigneduser: this.patient.ncm.first_name + ' ' + this.patient.ncm.last_name,
            patient: patient,
            type: 'Follow-Up',
            priority: 0,
            due_date: new Date(),
            notes: 'F/U with clinic for Rx Refill'
          };
          setTimeout(() => {
            this.dbTask.addTask(this.program_id, this.patient.id, task, this.user).then(() => {
              this.utility.presentToast('The remaining quantity is below the 15 unit threshold.  An urgent follow-up task was created.');
              loading.dismiss();
            });
          }, 3000);
        } else {
          loading.dismiss();
        }
      })
      .catch(err => {
        this.utility.presentToast('Unable to save the dispensing entry: ' + err);
        loading.dismiss();
      });
    } else {
      dispensing.id = this.dispensing_id;
      this.dbPatient.updateDispensing(this.program_id, this.patient, dispensing, this.user).then(() => {
        this.utility.presentToast('Dispening entry was successfully saved');
        this.cancel();
        console.log(dispensing.quantity_remaining);
        if (dispensing.quantity_remaining && dispensing.quantity_remaining <= 15 && this.patient.ncm) {
          const task = { 
            assigneduser_id: this.patient.ncm.id,
            assigneduser: this.patient.ncm.first_name + ' ' + this.patient.ncm.last_name,
            patient: patient,
            type: 'Follow-Up',
            priority: 0,
            due_date: new Date(),
            notes: 'F/U with clinic for Rx Refill'
          };
          setTimeout(() => {
            this.dbTask.addTask(this.program_id, this.patient.id, task, this.user).then(() => {
              this.utility.presentToast('The remaining quantity is below the 15 unit threshold.  An urgent follow-up task was created.');
              loading.dismiss();
              this.cancel();
            });
          }, 3000);
        } else {
          loading.dismiss();
          this.cancel();
        }
      })
      .catch(err => {
        this.utility.presentToast('Unable to save the dispensing entry: ' + err);
        loading.dismiss();
      });
    }
  }

  public objectComparisonFunction = function( option, value ) : boolean {
    if(option && value) return option.id === value.id;
    else return null;
  }

  edit(item) {
    this.dispensing_id = item.id;
    let newitem = { ...item };
    newitem.fill_date = item.fill_date.toDate();
    newitem.first_fill_date = item.first_fill_date.toDate();
    console.log(newitem);
    this.addeditDispensingForm.patchValue(newitem);
    this.dispensingPanelStatus = true;
    this.existing = true;
  }
  
  delete(item){
      let confirm = this.alertCtrl.create({
        title: "Delete Dispensing",
        message: "Are you sure you want to delete this dispensing entry?  If so, type 'delete' into the textbox to confirm",
        inputs: [
          {
            name: 'delete_text',
            placeholder: 'delete',
            type: 'text'
          }
        ],
        buttons: [
          {
            text: "Cancel",
            handler: () => {
              // console.log("Don't update");
            }
          },
          {
            text: "Delete Dispensing",
            handler: data => {
              let delete_text = data.delete_text;
              if(delete_text === 'delete') {
            
                this.dbPatient.deleteDispening(this.program_id, this.patient.id, item.id)
                .then(response => {
                  this.utility.presentToast('The dispensing entry was successfully deleted');
                })
                .catch(err => {
                  this.utility.presentToast('The dispensing entry could not be deleted');
                });
              } else {
                return;
              }
            }
          }
        ]
      });
      confirm.present();
      }


  cancel()  {
    this.dispensingPanelStatus = false;
    this.dispensing_id = null;
    this.existing = false;
    setTimeout(() => {
      this.formDispensingDirective.resetForm();
      this.addeditDispensingForm.reset();
    }, 0);
  }
}



// WEBPACK FOOTER //
// ./src/components/patient-dispensing/patient-dispensing.ts